import React, { useState } from "react";
import { useDispatch } from "react-redux";
import { LogtSearchAllCriteria } from "../models/logTSearchAllCriteria";
import { searchLogTsAllAction } from "../actions/logTActions";
import LoadingSpinner from "./LoadingSpinner";
import "../styles/CSVButton.scss"; 
import { appInsights } from "../appInsights";
import { FileSpreadsheet } from "lucide-react"; // Stylish CSV icon
import { useAuth } from '../context/AuthContext'; 
interface CSVButtonProps {
  searchCriteria: LogtSearchAllCriteria;
  className?: string;
  searchExecuted: boolean;
  user: any;
  onLoadingChange?: (loading: boolean) => void; // Callback for loading state changes
}

const CSVButton: React.FC<CSVButtonProps> = ({ searchCriteria, searchExecuted, className, user, onLoadingChange }) => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);

  const updateLoading = (newLoading: boolean) => {
    setLoading(newLoading);
    onLoadingChange?.(newLoading);
  };
  const isDisabled = !searchExecuted || loading;
  const userName = `${user.given_name} ${user.family_name}`;
  const userEmail = user.email || "Unknown";
  const userGroups = useAuth();
  const accessToken = userGroups?.userData?.access_token || '';

  const handleCSVDownload = async () => {
    if (isDisabled) return;
  
    updateLoading(true);
    try {
      const updatedSearchCriteria = { ...searchCriteria}; // Ensure we fetch up to 1000 records
      const data = await dispatch<any>(searchLogTsAllAction(updatedSearchCriteria, accessToken));
  
      if (!Array.isArray(data) || data.length === 0) {
        alert("No data available for CSV export.");
        updateLoading(false);
        return;
      }
  
      const csvHeader = [
        "Date & Time", "Direction", "Message Type", "SMI", "Medium",
        "Aircraft", "Flight#", "ORIG", "DEST", "Message"
      ];
  
      // Ensure all 1000 entries are included and messages are correctly formatted
      const csvRows = data.map((row: any) => [
        row.logtTS,
        row.direction,
        row.messageType,
        row.smi,
        row.medium,
        row.aircraftId,
        row.flightId,
        row.departureAirportIATA,
        row.arrivalAirportIATA,
        `"${row.logtContentMessage?.replace(/"/g, '""') || "N/A"}"` // Fix multi-line message formatting
      ]);
  
      const csvContent = [
        csvHeader.join(","), 
        ...csvRows.map((row) => row.join(","))
      ].join("\n");
  
      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.setAttribute("download", `acars_history_${new Date().toISOString()}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
  
      console.log(`CSV Downloaded Successfully (${data.length} entries)`);
    } catch (error) {
      console.error("Error fetching data for CSV:", error);
    } finally {
      updateLoading(false);
    }
  };
  

  return (
    <>
      {loading ? (
        <LoadingSpinner size="small" />
      ) : (
        <button
          onClick={handleCSVDownload}
          className={className ? className : "csv-button"}
          aria-label="Download CSV"
          disabled={isDisabled}
        >          <svg xmlns="http://www.w3.org/2000/svg" className="svg-icon" viewBox="0 0 1024 1024" version="1.1">
  <path d="M576 272V0H176C149.4 0 128 21.4 128 48v928c0 26.6 21.4 48 48 48h672c26.6 0 48-21.4 48-48V320H624c-26.4 0-48-21.6-48-48z m-192 288c0 8.84-7.16 16-16 16h-16c-17.68 0-32 14.32-32 32v64c0 17.68 14.32 32 32 32h16c8.84 0 16 7.16 16 16v32c0 8.84-7.16 16-16 16h-16c-53.02 0-96-42.98-96-96v-64c0-53.02 42.98-96 96-96h16c8.84 0 16 7.16 16 16v32z m88.54 208H448c-8.84 0-16-7.16-16-16v-32c0-8.84 7.16-16 16-16h24.54c11.9 0 20.82-7 20.82-13.24 0-2.6-1.5-5.32-4.24-7.68l-43.78-37.54c-16.94-14.44-26.66-34.96-26.66-56.28 0-42.6 38.04-77.24 84.82-77.24H528c8.84 0 16 7.16 16 16v32c0 8.84-7.16 16-16 16h-24.54c-11.9 0-20.82 7-20.82 13.24 0 2.6 1.5 5.32 4.24 7.68l43.78 37.54c16.94 14.44 26.66 34.96 26.66 56.28 0.02 42.58-38 77.24-84.78 77.24zM640 528v41.6c0 40.54 11.4 80.34 32 113.76 20.6-33.4 32-73.22 32-113.76V528c0-8.84 7.16-16 16-16h32c8.84 0 16 7.16 16 16v41.6c0 70.96-25.76 137.78-72.56 188.18-6.04 6.5-14.54 10.22-23.44 10.22s-17.4-3.72-23.44-10.22c-46.8-50.4-72.56-117.22-72.56-188.18V528c0-8.84 7.16-16 16-16h32c8.84 0 16 7.16 16 16z m242-318L686.2 14c-9-9-21.2-14-34-14H640v256h256v-12.2c0-12.6-5-24.8-14-33.8z"/>
  </svg>
      
        </button>
      )}
    </>
  );
};

export default CSVButton;
